import { useIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import styled from 'styled-components'
import PurchaseInvoiceFilters from './PurchaseInvoiceFilters'
import PurchaseInvoiceTable from './PurchaseInvoiceTable'
import { useEffect, useState } from 'react'
import type { PurchaseInvoiceFilters as PurchaseInvoiceFiltersType } from './types'
import PurchaseInvoiceDialog from './Dialog'
import usePurchaseInvoices from './usePurchaseInvoices'
import FloatingActionBtn from 'ui/components/FloatingActionBtn'
import { useSelectedFinanceCompany } from '../hooks/useSelectedFinanceCompany'
import useVats from '../Accounting/Voucher/useVats'
import { LoadingContainer } from '../components'
import LoadingState from 'ui/components/LoadingState'

const Container = styled.div`
	padding: 16px;
`

const ITEMS_PER_PAGE = 25

const PurchaseInvoiceView = () => {
	const intl = useIntl()

	const [addDialogVisible, setDialogVisible] = useState<boolean>(false)

	const { company } = useSelectedFinanceCompany()

	const queryParamId = useSelector((state: any) => {
		const qId = state.router?.location?.query?.id
		return qId ? parseInt(qId, 10) : undefined
	})

	const [filters, setFilters] = useState<PurchaseInvoiceFiltersType>({
		offset: 0,
		limit: ITEMS_PER_PAGE,
		orderByColumn: 'id',
		orderByDirection: 'desc',
	})

	const {
		purchaseInvoices,
		totalRecords,
		refresh: refreshPurchaseInvoices,
		loading: loadingPurchaseInvoices,
	} = usePurchaseInvoices({
		filters,
		companyUUID: company?.uuid,
		databaseSource: company?.integrations?.viiluTalous?.databaseSource,
	})

	const {
		vats,
		refresh: refreshVats,
		loading: loadingVats,
	} = useVats({
		companyUUID: company?.uuid,
		databaseSource: company?.integrations?.viiluTalous?.databaseSource,
	})

	const loading =
		(purchaseInvoices.length === 0 && loadingPurchaseInvoices) || loadingVats

	const loadingComponent = (
		<LoadingContainer>
			<LoadingState />
		</LoadingContainer>
	)

	const refresh = async () => {
		await Promise.all([refreshPurchaseInvoices(), refreshVats()])
	}

	useEffect(() => {
		// When navigating back we need to force refresh
		// this is a bit hacky
		if (!queryParamId) {
			refresh()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [queryParamId])

	const showDialog = () => {
		setDialogVisible(true)
	}

	const hideDialog = () => {
		refresh()
		setDialogVisible(false)
	}

	const renderFAB = () => {
		return (
			<FloatingActionBtn
				iconName={'add'}
				text={intl.formatMessage({
					defaultMessage: 'LUO OSTOLASKU',
					description: 'Create purchase invoice',
				})}
				action={showDialog}
			/>
		)
	}

	if (loading) {
		return loadingComponent
	}

	return (
		<Container>
			<PurchaseInvoiceFilters filters={filters} setFilters={setFilters} />
			<PurchaseInvoiceTable
				loading={loadingPurchaseInvoices}
				totalItemCount={totalRecords}
				itemsPerPage={ITEMS_PER_PAGE}
				filters={filters}
				setFilters={setFilters}
				purchaseInvoices={purchaseInvoices}
			/>

			{queryParamId && (
				<PurchaseInvoiceDialog
					vats={vats}
					purchaseInvoiceId={queryParamId}
					onHideURL={'/manager/finance/purchaseInvoices'}
					onHide={hideDialog}
				/>
			)}

			{addDialogVisible && (
				<PurchaseInvoiceDialog
					vats={vats}
					onHideURL={'/manager/finance/purchaseInvoices'}
					onHide={hideDialog}
				/>
			)}
			{renderFAB()}
		</Container>
	)
}

export default PurchaseInvoiceView
